import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
    AlertCircle,
    X,
    RefreshCw,
    Loader2,
    Eye,
} from 'lucide-react';
import EmployeeSearch from '../../components/common/EmployeeSearch';
import {
    fetchEmployeeLeaveSummary,
    selectLeaveSummary,
    selectLeaveTypeList,
    selectLeaveSummaryLoading,
    selectLeaveError,
    clearLeaveError,
} from '../../store/slices/leaveSlice';
import LeaveService from '../../services/LeaveService';

const fmt = (v) => Number(v || 0).toFixed(1);

// ─── Breakdown Modal ───────────────────────────────────────────────────
function BreakdownModal({ row, typeList, onClose }) {
    if (!row) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <div
                className="w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900">
                            {row.employeeName || 'Employee'}
                        </h3>
                        <p className="mt-0.5 text-xs text-gray-500">
                            Service No: <span className="font-medium text-gray-700">{row.serviceNo || '—'}</span>
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                                <th className="px-3 py-2 text-left font-semibold">Leave Type</th>
                                <th className="px-3 py-2 text-right font-semibold">Allocated</th>
                                <th className="px-3 py-2 text-right font-semibold">Used</th>
                                <th className="px-3 py-2 text-right font-semibold">Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {typeList.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-400">
                                        No leave types available.
                                    </td>
                                </tr>
                            ) : (
                                typeList.map((t) => {
                                    const d = row.types?.[t.code];
                                    return (
                                        <tr key={t.code} className="border-b border-gray-100">
                                            <td className="px-3 py-2 font-medium text-gray-800">{t.name}</td>
                                            <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                                                {fmt(d?.allocated)}
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums font-medium text-amber-600">
                                                {fmt(d?.used)}
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-600">
                                                {fmt(d?.balance)}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                        {typeList.length > 0 && (
                            <tfoot>
                                <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                                    <td className="px-3 py-2.5 text-gray-900">Totals</td>
                                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">
                                        {fmt(row.totalAllocated)}
                                    </td>
                                    <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">
                                        {fmt(row.totalUsed)}
                                    </td>
                                    <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">
                                        {fmt(row.totalBalance)}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
}

// ─── Main Component ────────────────────────────────────────────────────
export default function LeaveBalance() {
    const dispatch = useDispatch();
    const store = useStore();

    // ── Redux state ────────────────────────────────────────────────────
    const leaveSummary = useSelector(selectLeaveSummary);
    const leaveTypeList = useSelector(selectLeaveTypeList);
    const isLoadingSummary = useSelector(selectLeaveSummaryLoading);
    const error = useSelector(selectLeaveError);

    // ── Local state ────────────────────────────────────────────────────
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState({
        employeeId: '__ALL__',
        name: 'All Employees',
        isAll: true,
    });
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [breakdownRow, setBreakdownRow] = useState(null);

    // ── Fetch employees ────────────────────────────────────────────────
    const fetchEmployees = useCallback(async () => {
        setLoadingEmployees(true);
        try {
            const state = store.getState();
            const reduxEmployees = state.employees?.employees;

            if (reduxEmployees && Array.isArray(reduxEmployees) && reduxEmployees.length > 0) {
                setEmployees(reduxEmployees);
                return;
            }

            const response =
                (await LeaveService.GetEmployees?.()) ||
                (await fetch('/api/employees').then((r) => r.json()));
            const empList = response?.ResultSet || response?.data || response || [];
            setEmployees(Array.isArray(empList) ? empList : []);
        } catch (err) {
            console.error('[LeaveBalance] Failed to fetch employees:', err);
            setEmployees([]);
        } finally {
            setLoadingEmployees(false);
        }
    }, [store]);

    const refreshSummary = useCallback(() => {
        dispatch(fetchEmployeeLeaveSummary());
    }, [dispatch]);

    useEffect(() => {
        fetchEmployees();
        refreshSummary();
    }, [fetchEmployees, refreshSummary]);

    // ── Employee options for search ────────────────────────────────────
    const employeeOptions = useMemo(() => {
        if (!employees || !Array.isArray(employees)) return [];
        return employees
            .map((emp) => {
                const serviceNo =
                    emp.p_ced_service_no ||
                    emp.HED_SERVICE_NO ||
                    emp.HED_CED_SERVICE_NO ||
                    emp.serviceNo ||
                    '';
                const name =
                    emp.p_report_name ||
                    emp.HED_REPORT_NAME ||
                    emp.reportName ||
                    `${emp.p_first_name || emp.HED_FIRST_NAME || ''} ${emp.p_last_name || emp.HED_LAST_NAME || ''}`.trim() ||
                    'Unnamed Employee';
                return {
                    employeeId: String(serviceNo || emp.HED_EMPLOYEE_ID || emp.employeeId || ''),
                    serviceNo: String(serviceNo),
                    name,
                    department: emp.p_department_name || emp.HDD_DEPARTMENT_NAME || '',
                };
            })
            .filter((e) => e.employeeId);
    }, [employees]);

    // ── Filtered rows ──────────────────────────────────────────────────
    const filteredData = useMemo(() => {
        if (!leaveSummary || leaveSummary.length === 0) return [];
        if (!selectedEmployee || selectedEmployee.isAll) return leaveSummary;
        return leaveSummary.filter((item) => item.serviceNo === selectedEmployee.serviceNo);
    }, [leaveSummary, selectedEmployee]);

    const handleEmployeeSelect = (employee) => {
        if (!employee) {
            setSelectedEmployee({ employeeId: '__ALL__', name: 'All Employees', isAll: true });
        } else {
            setSelectedEmployee(employee);
        }
        dispatch(clearLeaveError());
    };

    const handleRefresh = () => refreshSummary();
    const isLoading = loadingEmployees || isLoadingSummary;

    // ── Type groups (for header + cells) ───────────────────────────────
    const typeGroups = useMemo(
        () =>
            (leaveTypeList || []).map((type) => ({
                code: type.code,
                name: type.name,
            })),
        [leaveTypeList]
    );

    // ── Render ─────────────────────────────────────────────────────────
    return (
        <div className="p-6 space-y-4">
            {/* ── Filters ────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[220px] max-w-md">
                    <EmployeeSearch
                        value={selectedEmployee}
                        onSelect={handleEmployeeSelect}
                        employees={employeeOptions}
                        loading={loadingEmployees}
                        showAllOption
                    />
                </div>
                <div>
                    <button
                        onClick={handleRefresh}
                        disabled={isLoading}
                        className="flex shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-surface-50 disabled:opacity-50"
                    >
                        <RefreshCw size={15} className={isLoading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* ── Error ──────────────────────────────────────────────── */}
            {error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <AlertCircle size={14} />
                    {error}
                    <button
                        onClick={() => dispatch(clearLeaveError())}
                        className="ml-auto text-red-500 hover:text-red-700"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}

            {/* ── Table ──────────────────────────────────────────────── */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 size={32} className="animate-spin text-primary-600" />
                    </div>
                ) : filteredData.length === 0 ? (
                    <div className="px-5 py-10 text-center text-sm text-gray-400">
                        No leave balances found.
                    </div>
                ) : (

                    <table className="w-full border-separate border-spacing-0 text-sm">
                        {/* ===== Two-row grouped header ===== */}
                        <thead className="sticky -top-6 z-20 bg-gray-50 shadow-[0_1px_0_0_rgb(229_231_235)]">
                            {/* Row 1 — group labels */}
                            <tr className="bg-gray-50">
                                <th
                                    rowSpan={2}
                                    className="sticky left-0 top-0 z-30 whitespace-nowrap bg-gray-50 px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 shadow-[1px_0_0_0_rgb(229_231_235)]"
                                >
                                    Employee
                                </th>
                                <th
                                    rowSpan={2}
                                    className="whitespace-nowrap bg-gray-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                                >
                                    Service No
                                </th>
                                <th
                                    rowSpan={2}
                                    className="whitespace-nowrap bg-gray-50 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500"
                                >
                                    Year
                                </th>

                                {typeGroups.map((g) => (
                                    <th
                                        key={`group_${g.code}`}
                                        colSpan={3}
                                        className="whitespace-nowrap border-l border-gray-200 bg-gray-50 px-2 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-600"
                                    >
                                        {g.name}
                                    </th>
                                ))}

                                <th
                                    colSpan={4}
                                    className="whitespace-nowrap border-l border-gray-200 bg-gray-50 px-2 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-700"
                                >
                                    Totals
                                </th>
                            </tr>

                            {/* Row 2 — sub-column labels */}
                            <tr className="bg-gray-50">
                                {typeGroups.flatMap((g) =>
                                    ['Alloc', 'Used', 'Bal'].map((label, i) => (
                                        <th
                                            key={`${g.code}_${label}`}
                                            className={`whitespace-nowrap bg-gray-50 px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wide text-gray-400 ${i === 0 ? 'border-l border-gray-200' : ''
                                                }`}
                                        >
                                            {label}
                                        </th>
                                    ))
                                )}

                                <th className="whitespace-nowrap border-l border-gray-200 bg-gray-50 px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                    Alloc
                                </th>
                                <th className="whitespace-nowrap bg-gray-50 px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                    Used
                                </th>
                                <th className="whitespace-nowrap bg-gray-50 px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                    Bal
                                </th>
                                <th className="whitespace-nowrap bg-gray-50 px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                    View
                                </th>
                            </tr>
                        </thead>

                        {/* ===== Body ===== */}
                        <tbody>
                            {filteredData.map((row, index) => {
                                const isOdd = index % 2 === 1;
                                const stickyBg = isOdd ? 'bg-surface-50' : 'bg-white';

                                return (
                                    <tr
                                        key={row.serviceNo || `row-${index}`}
                                        className={isOdd ? 'bg-surface-50/40' : ''}
                                    >
                                        {/* Employee — sticky left */}
                                        <td
                                            className={`sticky left-0 z-10 whitespace-nowrap ${stickyBg} px-5 py-3 shadow-[1px_0_0_0_rgb(243_244_246)]`}
                                        >
                                            <span className="font-semibold text-gray-900">
                                                {row?.employeeName || '—'}
                                            </span>
                                        </td>

                                        {/* Service No */}
                                        <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                                            {row?.serviceNo || '—'}
                                        </td>

                                        {/* Year */}
                                        <td className="whitespace-nowrap px-3 py-3 text-center text-gray-500">
                                            {row?.leaveYear || '—'}
                                        </td>

                                        {/* Per-type: Alloc / Used / Bal */}
                                        {typeGroups.map((g, gi) => {
                                            const t = row?.types?.[g.code];
                                            return (
                                                <React.Fragment key={g.code}>
                                                    <td
                                                        className={`whitespace-nowrap px-2 py-3 text-center tabular-nums text-gray-600 ${gi >= 0 ? 'border-l border-gray-100' : ''
                                                            }`}
                                                    >
                                                        {t ? fmt(t.allocated) : '—'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-2 py-3 text-center tabular-nums text-amber-600">
                                                        {t ? fmt(t.used) : '—'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-2 py-3 text-center tabular-nums font-semibold text-emerald-600">
                                                        {t ? fmt(t.balance) : '—'}
                                                    </td>
                                                </React.Fragment>
                                            );
                                        })}

                                        {/* Totals group */}
                                        <td className="whitespace-nowrap border-l border-gray-200 px-2 py-3 text-center tabular-nums font-medium text-gray-700">
                                            {fmt(row.totalAllocated)}
                                        </td>
                                        <td className="whitespace-nowrap px-2 py-3 text-center tabular-nums font-medium text-amber-600">
                                            {fmt(row.totalUsed)}
                                        </td>
                                        <td className="whitespace-nowrap px-2 py-3 text-center tabular-nums font-semibold text-emerald-600">
                                            {fmt(row.totalBalance)}
                                        </td>
                                        <td className="whitespace-nowrap px-2 py-3 text-center">
                                            <button
                                                onClick={() => setBreakdownRow(row)}
                                                className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary-600"
                                                title="View leave-type-wise breakdown"
                                                aria-label={`View breakdown for ${row.employeeName}`}
                                            >
                                                <Eye size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                )}
            </div>

            {/* ── Footer ─────────────────────────────────────────────── */}
            {!isLoading && !error && filteredData.length > 0 && (
                <div className="flex items-center justify-between border-t border-gray-200 px-5 py-4">
                    <div className="text-sm text-gray-500">
                        Showing <strong className="text-gray-800">{filteredData.length}</strong>{' '}
                        {filteredData.length === 1 ? 'entry' : 'entries'}
                    </div>
                    <div className="hidden text-xs text-gray-400 md:flex items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-gray-400" /> Allocated
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-amber-500" /> Used
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full bg-emerald-500" /> Balance
                        </span>
                    </div>
                </div>
            )}

            {/* ── Breakdown Modal ────────────────────────────────────── */}
            {breakdownRow && (
                <BreakdownModal
                    row={breakdownRow}
                    typeList={leaveTypeList}
                    onClose={() => setBreakdownRow(null)}
                />
            )}
        </div>
    );
}